/**
 * Placing a party into the rooms the guest chose, pure (batch-29, item R1; plan §4.6). It runs in the browser; the
 * server only checks the result. A greedy fill on purpose: nothing cleverer is wanted, the guest moves people after.
 */
import { bedDefault, type BedSettings, type BedState, type RoomBeds, type RoomPeople } from './beds';

/** Counts per age-group key, e.g. `{ adult: 2, infant: 1 }`. */
export type Counts = Record<string, number>;
export interface PlacedRoom { people: Counts; state: BedState }
export interface Placement {
  rooms: PlacedRoom[];
  /** people no room could take, per group */
  pool: Counts;
  /** how many people are in the pool */
  left: number;
}

const total = (c: Counts): number => Object.values(c).reduce((s, n) => s + n, 0);

/** A room's people as the bed rule sees them. */
export function roomPeople(people: Counts, cribKey: string | null): RoomPeople {
  const babies = cribKey ? people[cribKey] ?? 0 : 0;
  return { others: total(people) - babies, babies };
}

/** The cots and cribs a room gets for the people in it; null when the room cannot take them. An empty room needs nothing. */
export function roomState(type: RoomBeds, s: BedSettings, people: Counts, cribKey: string | null): BedState | null {
  if (total(people) === 0) return { cots: 0, cribs: 0 };
  return bedDefault(type, s, roomPeople(people, cribKey));
}

/**
 * Spreads the party across the rooms. `groupOrder` is the event's groups youngest first; people are placed oldest
 * group first and the crib group last, so every room receives a non-baby before any baby.
 */
export function placeParty(rooms: readonly RoomBeds[], s: BedSettings, party: Counts, groupOrder: readonly string[], cribKey: string | null): Placement {
  const order = [...groupOrder].reverse().filter((k) => k !== cribKey);
  for (const k of Object.keys(party)) if (k !== cribKey && !order.includes(k)) order.push(k);
  if (cribKey) order.push(cribKey);
  const queue = order.flatMap((k) => Array.from({ length: Math.max(0, party[k] ?? 0) }, () => k));
  const takes = (type: RoomBeds, people: Counts, key: string): boolean =>
    bedDefault(type, s, roomPeople({ ...people, [key]: (people[key] ?? 0) + 1 }, cribKey)) !== null;

  // pass 1 — spread: each person goes to the first room at or after the pointer that still takes them
  const spread = (): { people: Counts[]; pool: string[] } => {
    const people = rooms.map((): Counts => ({}));
    const pool: string[] = [];
    let at = 0;
    for (const key of queue) {
      let put = -1;
      for (let n = 0; n < rooms.length && put < 0; n++) {
        const r = (at + n) % rooms.length;
        if (takes(rooms[r]!, people[r]!, key)) put = r;
      }
      if (put < 0) { pool.push(key); continue; }
      people[put]![key] = (people[put]![key] ?? 0) + 1;
      at = (put + 1) % rooms.length;
    }
    return { people, pool };
  };
  // pass 2 — only when someone was left out: the largest rooms first, each filled as far as it goes
  const fill = (): { people: Counts[]; pool: string[] } => {
    const people = rooms.map((): Counts => ({}));
    let waiting = [...queue];
    const bySize = rooms.map((_, i) => i).sort((a, b) => (rooms[b]!.maxPeople - rooms[a]!.maxPeople) || (a - b));
    for (const r of bySize) {
      const next: string[] = [];
      for (const key of waiting) {
        if (takes(rooms[r]!, people[r]!, key)) people[r]![key] = (people[r]![key] ?? 0) + 1;
        else next.push(key);
      }
      waiting = next;
    }
    return { people, pool: waiting };
  };

  let got = rooms.length ? spread() : { people: [] as Counts[], pool: [...queue] };
  if (got.pool.length && rooms.length) {
    const second = fill();
    if (second.pool.length < got.pool.length) got = second;
  }
  const pool: Counts = {};
  for (const key of got.pool) pool[key] = (pool[key] ?? 0) + 1;
  return {
    rooms: got.people.map((people, i) => ({ people, state: roomState(rooms[i]!, s, people, cribKey) ?? { cots: 0, cribs: 0 } })),
    pool,
    left: got.pool.length,
  };
}
